import { getVipTypeByUser, PERSONAL_NO_AUTH } from "@/constants/vip";
import useAppStore from "@/context/store";
import { gdOssUrl } from "@/utils/imageUrl";
import classNames from "classnames";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Case, Switch } from "react-if";
import { cancelSubscription, getCertList, updateCert } from "../../../services";
import AuthSubject from "../../AuthSubject/form";
import HBMessage from "../../HBMessage";
import HBModal from "../../HBModal";
import PayResult from "../payResult";
import { useDialogContext, VipDialogContext } from "../shared/configContext";
import VipHeader from "../shared/header3";
import SubjectConfirm from "../subjectConfirm";
import PublicTransfer from "../Transfer";
import styles from "./index.module.less";
import VipDetail from "./vipDetail";

/**
 * 打开会员弹框
 * @param {Object} props
 * @param {'company'|'personal'=} props.type  当page为vipDetail，才需要指定会员类型
 * @param {Object=} props.userRight  当page为vipDetail，才需要指定会员类型
 * @param {Array<string>} [props.selectedPkg] props.selectedPkg  指定选中的套餐，传商品code集合
 * @param {string=} props.defaultSelectPlusSku  指定选中的换购商品，传商品code或者sku_id
 * @param {string=} props.versionTab 选中Version（基础授权、高级授权）(个人全站、单品类会员)使用
 * @param {boolean=} props.hideSingleCategory 是否隐藏单品类会员
 * @param {(order: Object)=>any=} props.onFinish 流程结束，且订单已完成， 授权主体有没有填写未知
 * @param {(step: 'vipDetail' )=> any=} props.onAborted 订单未支付,流程中止
 * @param {(order: Object)=>any=} props.onPaid 订单支付完成，但流程还在继续
 * @param {()=>void=} props.onOk 所有流程完成,且 且弹框关闭
 * @param {()=>void=} props.onCancel 弹框关闭 参数 step 当前页面的位置
 * @param {()=>void=} props.onUnSubscribe 弹框关闭 参数 step 当前页面的位置
 */
export default function BuyVipDialog({
  type = "personal",
  isUpgrade,
  userRight,
  selectedPkg,
  defaultSelectPlusSku,
  hideSingleCategory,
  versionTab,
  onOk,
  onCancel,
  onPaid,
  onFinish,
  onAborted,
  onUnSubscribe,
  ...props
}) {
  const { user, refreshVips, refreshUser } = useAppStore(
    ({ user, refreshVips, refreshUser }) => ({
      user,
      refreshVips,
      refreshUser,
    })
  );

  const context = useDialogContext(userRight);
  const subscription = context.rightData?.subscription;

  // 步骤
  const [step, setStep] = useState("vipDetail");

  // 当前购买tab的会员类型
  const [vipType, setVipType] = useState(type);

  // 当前用户为企业会员时，直接跳转到企业会员tab
  useEffect(() => {
    if (getVipTypeByUser(user) === "company") {
      setVipType("company");
    }
  }, [user]);

  const [order, setOrder] = useState({});
  const [subject, setSubject] = useState({});

  const next = useCallback(
    (params) => {
      if (step === "payResult") setStep("authSubject");
      if (step === "authSubject") {
        setSubject(params);
        setStep("subjectConfirm");
      }
    },
    [step]
  );

  const onOrderDone = useCallback(
    async (order) => {
      onPaid && onPaid(order);
      setOrder(order);
      setStep("payResult");
      await refreshVips();
      refreshUser();

      // 购买其他会员套餐，自动取消订阅
      if (subscription && subscription.length) {
        subscription.forEach((sub) => {
          cancelSubscription(sub.id, "用户不想用了");
        });
      }
      // 重置会员到期提醒
      window.localStorage.removeItem(`ExpiredVipDialogKey_${user.user_id}`);
    },
    [onPaid, refreshVips, refreshUser, subscription, user?.user_id]
  );

  // 授权主体信息提交
  const onSubmit = useCallback(async () => {
    const certSubject = await getCertList({
      source_no: order.order_no,
    });

    if (!certSubject?.list?.length > 0) {
      HBMessage.error("获取授权信息失败");
      return;
    }

    const result = await updateCert({
      id: certSubject.list[0].id,
      subject,
    });

    if (result.err) return;

    HBMessage.success("提交成功");
    onOk && onOk();
  }, [onOk, order, subject]);

  const wrapStyle = useMemo(() => {
    return {
      width: 960,
      // minHeight: step === "vipDetail" ? 680 : 772,
      // maxHeight: "100vh",
      overflow: "hidden",
      backgroundImage: `url(${gdOssUrl(
        "https://st0.dancf.com/static/02/202202110238-4e8a.jpg",
        {
          width: 960,
        }
      )})`,
      backgroundSize: "cover",
    };
  }, []);

  const onCancelWrap = useCallback(() => {
    onCancel && onCancel(step);

    // 在支付成功页后，调用 onFinish
    if (!["vipDetail"].includes(step)) {
      onFinish && onFinish(order);
    } else {
      // 终止
      onAborted && onAborted(step);
    }
  }, [onCancel, onFinish, order, step, onAborted]);

  const [transferProps, setTransferProps] = useState({});
  const onTransfer = useCallback((transferProps) => {
    setStep("transfer");
    setTransferProps(transferProps);
  }, []);

  const onCancelTransfer = useCallback(() => {
    setStep("vipDetail");
  }, []);

  const buyDialogConfig = context.config;
  const headConfig = useMemo(() => {
    const activity = buyDialogConfig?.[vipType]?.activity;

    if (activity?.bgImage?.length) {
      // 倒计时结束
      const isEnd = new Date(activity.countDown?.targetDate) < Date.now();

      if (!isEnd) {
        return buyDialogConfig[vipType]?.activity;
      }
    }

    return buyDialogConfig?.header;
  }, [buyDialogConfig, vipType]);

  // 非商用授权订单
  const isNonCommercial = useMemo(() => {
    if (order) {
      return order.skus?.some((sku) =>
        sku.ref_body?.right_packages?.find(
          (item) => item.identification === PERSONAL_NO_AUTH
        )
      );
    }

    return false;
  }, [order]);

  return (
    <VipDialogContext.Provider value={context}>
      <HBModal
        className={classNames(styles.dialog, {
          [styles.step1]: step === "vipDetail",
        })}
        {...props}
        width={960}
        footer={null}
        bodyStyle={{ padding: 0, overflowX: "auto" }}
        onCancel={onCancelWrap}
      >
        <div style={wrapStyle}>
          {["vipDetail", "choice", "pay", "transfer"].includes(step) && (
            <VipHeader user={user} config={headConfig} />
          )}
          <Switch>
            <Case condition={step === "vipDetail"}>
              <div key={step} style={{ marginTop: 90 }}>
                <VipDetail
                  user={user}
                  defaultVipType={isUpgrade ? "company" : vipType}
                  selectedPkg={selectedPkg}
                  defaultSelectPlusSku={defaultSelectPlusSku}
                  hideSingleCategory={hideSingleCategory}
                  versionTab={versionTab}
                  typeChange={setVipType}
                  onUnSubscribe={onUnSubscribe}
                  onCancel={onCancelWrap}
                  onOrderDone={onOrderDone}
                  onTransfer={onTransfer}
                />
              </div>
            </Case>

            <Case condition={step === "transfer"}>
              <PublicTransfer
                {...transferProps}
                onCancelTransfer={onCancelTransfer}
              />
            </Case>

            <Case condition={step === "payResult"}>
              <PayResult
                key={step}
                onNext={next}
                order={order}
                style={{ height: 772 }}
                show={!isNonCommercial}
                data-content-source={
                  vipType === "company" ? "企业VIP" : "个人VIP"
                }
                data-dialog-title={
                  order?.trade_mode === "promotion"
                    ? "会员升级支付成功"
                    : "会员支付成功弹框"
                }
                data-gd-expose="dialog_expose"
              />
            </Case>
            <Case condition={step === "authSubject"}>
              <div
                key={step}
                className={styles.subjectWrap}
                data-dialog-title="会员购买后填写授权信息弹框"
                data-gd-expose="dialog_expose"
              >
                <h2>填写被授权方</h2>
                <AuthSubject
                  type={vipType}
                  initValue={subject}
                  scene="buyVip"
                  onSubmit={next}
                  onCancel={onCancelWrap}
                />
              </div>
            </Case>
            <Case condition={step === "subjectConfirm"}>
              <SubjectConfirm
                key={step}
                subject={subject}
                onBack={() => {
                  setStep("authSubject");
                }}
                onOk={onSubmit}
                data-dialog-title="会员购买后授权信息确认弹框"
                data-gd-expose="dialog_expose"
              />
            </Case>
          </Switch>
        </div>
      </HBModal>
    </VipDialogContext.Provider>
  );
}
